import { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Fab, List, ListItem, ListItemIcon, ListItemText, Stack, Typography, Divider } from '@mui/material'
import { Add, AdminPanelSettings } from '@mui/icons-material'
import { com } from '@eidu/web'
import { useSortedEntityTypes } from '../../io/useEntityTypes'
import PermissionError from './PermissionError'
import ListItemButtonWithNavigation from '../../components/input/ListItemButtonWithNavigation'
import { iconForEntityTypeKind } from './kindViewRegistry'
import { isAdminUserType } from '../../util/entityType/isAdminUserType'
import useHasGlobalPermissions from '../permission/useHasGlobalPermissions'
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import { buildSidebarSectionsFromEntityTypes } from '../../components/navigation/SidebarSection'
import type { SidebarGroup } from '../../domain/kindRegistry'

const SIDEBAR_GROUP_ORDER: readonly SidebarGroup[] = ['entity', 'document', 'asset']

const EntityTypesPage = () => {
  const navigate = useNavigate()

  const {
    hasPermission: hasGlobalPermission,
    error: permissionsError,
    loading: permissionsLoading,
  } = useHasGlobalPermissions(GlobalAction.ManageEntityTypes)

  const entityTypes = useSortedEntityTypes()

  const adminEntityType = entityTypes?.find(
    (type) => type.kind.equals(EntityTypeKind.Companion.User) && isAdminUserType(type.name)
  )

  type SectionItem = { id: EntityType['id']; name: string; icon: ReactNode }
  type Section = { title: string; items: SectionItem[]; dividerBefore?: boolean }

  const adminItems: SectionItem[] = []
  if (adminEntityType) {
    adminItems.push({
      id: adminEntityType.id,
      name: 'Admin Users',
      icon: <AdminPanelSettings />,
    })
  }

  const adminEntityTypeId = adminEntityType?.id.toString()
  const visibleEntityTypes = (entityTypes ?? []).filter((type) => type.id.toString() !== adminEntityTypeId)

  const kindSections = buildSidebarSectionsFromEntityTypes(
    visibleEntityTypes,
    (type): SectionItem => ({ id: type.id, name: type.name, icon: iconForEntityTypeKind(type.kind) })
  )

  const sections: Section[] = []

  const pushSection = (section: Section) => {
    if (!section.items.length) return
    sections.push({ ...section, dividerBefore: sections.length > 0 })
  }

  pushSection({ title: 'Administration', items: adminItems })

  for (const sidebarGroup of SIDEBAR_GROUP_ORDER) {
    const groupSections = kindSections.filter((s) => s.sidebarGroup === sidebarGroup && s.items.length > 0)
    groupSections.forEach((s) => pushSection({ title: s.title, items: s.items }))
  }

  if (permissionsLoading) return null

  if (permissionsError) return <PermissionError missingPermittedActions={[GlobalAction.ManageEntityTypes]} />

  return (
    (hasGlobalPermission(GlobalAction.ManageEntityTypes) && (
      <>
        <Fab
          color="primary"
          aria-label="Create entity type"
          onClick={() => navigate('new', { relative: 'path' })}
          sx={{
            position: 'fixed',
            bottom: (theme) => theme.spacing(2),
            right: (theme) => theme.spacing(2),
          }}
        >
          <Add />
        </Fab>
        <Stack paddingX={3} paddingY={3} spacing={2.5}>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Types
          </Typography>
          {sections.map(({ title, items }) => (
            <Stack key={title} spacing={0.25}>
              <Typography variant="overline" sx={{ letterSpacing: 1.5, fontSize: '0.7rem', lineHeight: 1, pb: 0.25 }}>
                {title}
              </Typography>
              <Divider sx={{ mb: 0.5 }} />
              <List dense disablePadding>
                {items.map(({ name, id, icon }, index) => (
                  <ListItem key={`entity-type-${id}`} disablePadding sx={{ py: index === 0 ? 0 : 0.25 }}>
                    <ListItemButtonWithNavigation path={`/types/${id.toString()}`}>
                      <ListItemIcon sx={{ minWidth: 30 }}>{icon}</ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: '0.9rem' }} primary={name} />
                    </ListItemButtonWithNavigation>
                  </ListItem>
                ))}
              </List>
            </Stack>
          ))}
        </Stack>
      </>
    )) ||
    PermissionError({ missingPermittedActions: [GlobalAction.ManageEntityTypes] })
  )
}

export default EntityTypesPage
